<template>
  <div v-if="useCompetitors" :class="classes.container">
    <Competitors
      :eventId="eventId"
      :displayName="useTeamNames"
      :forceTeamName="teamName"
      :customSvgPaths="customSvgPaths"
      :customSvgAspectRatio="customSvgAspectRatio"
      :layout="layout"
      :class="[classes.competitors, 'competitors', layoutClass]"
    />
  </div>

  <LinkWrapper
    v-else
    :href="eventLink"
    target="_blank"
    :class="classes.linkWrapper"
    @click="() => handleClick()"
  >
    <DynamicText
      tag="div"
      styleGroup="Event Name"
      :get="fixtureNameStyle.get"
      :set="fixtureNameStyle.set"
      :class="classes.eventName"
    >
      {{ eventName }}
    </DynamicText>
  </LinkWrapper>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import Competitors from "./CompetitorsBadges.vue";
import DynamicText from "./DynamicText.vue";
import LinkWrapper from "./LinkWrapper.vue";
import { composeEvent } from "@fresh8/ad-data-composition/src/betData";
import { Brand } from "@fresh8/types/src";

import { injectCardNumber, useAnalytics } from "../hooks/useAnalytics";
import {
  injectTemplateSize,
  LARGE_RESPONSIVE,
  SMALL_RESPONSIVE
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectCarouselContext } from "../lib/provideInjectors";
import { injectTeamColors } from "../lib/teamBranding";

const useStyles = createUseStyles({
  container: {
    position: "relative",
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr 1fr"
  },
  competitors: {
    display: "flex",
    "& .banner": { width: "100%", minWidth: "100px", maxWidth: "200px" },

    "&:last-of-type": {
      justifyContent: "flex-end"
    },

    "&.vs": {
      zIndex: 1,
      position: "absolute",
      height: "90%",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)"
    }
  },
  eventName: {
    fontSize: "13px",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    paddingBottom: 0
  },
  linkWrapper: {
    gridColumn: "1 / -1"
  }
});

export default defineComponent({
  name: "EventName",
  components: {
    Competitors,
    DynamicText,
    LinkWrapper
  },
  props: {
    eventId: { type: String, required: true },
    useTeamNames: { type: Boolean, default: false },
    forceTeamName: {
      type: String as PropType<"full" | "short" | "micro">,
      default: undefined
    },
    forceEventName: { type: Boolean, default: false },
    layout: {
      type: String as PropType<"column" | "row">,
      default: "row"
    },
    customSvgPaths: {
      type: Object as PropType<{ left?: string; right?: string }>,
      required: false,
      default: {
        left: "",
        right: ""
      } as Record<"left" | "right", string>
    },
    customSvgAspectRatio: {
      type: String,
      required: false,
      default: ""
    }
  },
  setup(props) {
    const template = injectTemplateSize();
    const carouselContext = injectCarouselContext();
    const event = composeEvent(props.eventId);
    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      { ...props, cardNumber: cardNumber },
      "Event Name"
    );
    const templateSize = injectTemplateSize();
    const teamColorsContext = injectTeamColors();

    const useTeamIcons = computed(
      () => teamColorsContext.teamColorsAvailable.value
    );

    const useCompetitors = computed(() => {
      return (
        !props.forceEventName &&
        (event.value?.competitors.home.name.full?.length || 0) > 0 &&
        (event.value?.competitors.away.name.full?.length || 0) > 0
      );
    });

    const teamName = computed(() => {
      switch (templateSize?.size.value.name) {
        case SMALL_RESPONSIVE.name:
          return useTeamIcons.value ? "micro" : "short";
        case LARGE_RESPONSIVE.name:
          return useTeamIcons.value ? "short" : props.forceTeamName;
        default:
          return props.forceTeamName;
      }
    });

    const reactiveStyles = computed(() => ({
      isCarousel: carouselContext.value.isCarousel
    }));
    const classes = useStyles(reactiveStyles);

    return {
      teamName,
      classes,
      layoutClass: computed(() => template?.size.value.name),
      useCompetitors,
      eventLink: computed(() => event?.value?.eventUrl),
      eventName: computed(() => event?.value?.name),
      handleClick,
      fixtureNameStyle: {
        get: (brand: Brand | undefined) =>
          brand?.adTemplate.market.teamNameFont || "",
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>
